import { createContext, useContext, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { trpc } from "./trpc";
import { sairDoAdmin } from "./login-do-admin";
import { ConfigurarMfa, EntrarComCodigo } from "@/components/mfa/ConfigurarMfa";

type AdminUser = {
  id: string;
  name: string | null;
  email: string;
  image: string | null;
  platformRole: "MEMBER" | "ADMIN";
};

type AuthContextValue = {
  user: AdminUser | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

async function signOut() {
  try {
    await sairDoAdmin();
  } finally {
    window.location.href = "/login";
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const meQuery = trpc.me.current.useQuery(undefined, {
    retry: false,
    staleTime: 60_000,
  });

  const value: AuthContextValue = {
    user: meQuery.data ?? null,
    isLoading: meQuery.isLoading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** Gate: exige sessao + platformRole=ADMIN + segundo fator. Nao-ADMIN ve 403. */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const mfa = trpc.mfaDoAdmin.situacao.useQuery(undefined, { enabled: user?.platformRole === "ADMIN", retry: false });

  if (isLoading || (user?.platformRole === "ADMIN" && mfa.isLoading)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-admin-700 border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  if (user.platformRole !== "ADMIN") {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-admin-50 p-8 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 text-red-700">
          <span className="text-2xl">⛔</span>
        </div>
        <div>
          <h1 className="text-xl font-bold text-admin-900">Acesso restrito</h1>
          <p className="mt-1 text-sm text-admin-700">
            Esta area e apenas para administradores da plataforma.
          </p>
        </div>
        <button
          type="button"
          onClick={signOut}
          className="mt-2 rounded-md bg-admin-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-admin-800"
        >
          Sair
        </button>
      </div>
    );
  }

  if (!mfa.data?.naSessao) return mfa.data?.ativo ? <EntrarComCodigo /> : <ConfigurarMfa />;

  return <>{children}</>;
}
